import store from "@/state/store";
import keycloakService from "@/services/keycloak";
import { CHIAVE_ULTIMA_USCITA } from "@/services/diagnosticaUscita";
import { allAppRoutes } from "@/apps";

// Le viste del portale dei proprietari (F6, §11 di f6-progetto.md), in un chunk loro ("portale"): il proprietario sul
// telefono non scarica le pagine del gestore, e il gestore scarica queste solo aprendo l'anteprima. Le stesse viste
// servono il portale (/portale/...) e l'anteprima del gestore (/gestione/portale/anteprima/:id/...).
const VISTE_PORTALE = {
  home: () => import(/* webpackChunkName: "portale" */ "../views/portale/home.vue"),
  prenotazioni: () => import(/* webpackChunkName: "portale" */ "../views/portale/prenotazioni.vue"),
  prenotazione: () => import(/* webpackChunkName: "portale" */ "../views/portale/prenotazione.vue"),
  calendario: () => import(/* webpackChunkName: "portale" */ "../views/portale/calendario.vue"),
  conti: () => import(/* webpackChunkName: "portale" */ "../views/portale/conti.vue"),
  distinta: () => import(/* webpackChunkName: "portale" */ "../views/portale/distinta.vue"),
  documenti: () => import(/* webpackChunkName: "portale" */ "../views/portale/documenti.vue"),
  profilo: () => import(/* webpackChunkName: "portale" */ "../views/portale/profilo.vue"),
};

/** Le rotte del portale sotto la loro radice, come coppie [sottopercorso, vista]. */
const ROTTE_PORTALE = [
  ["", "home"], ["/prenotazioni", "prenotazioni"], ["/prenotazioni/:id", "prenotazione"], ["/calendario", "calendario"],
  ["/conti", "conti"], ["/distinte/:id", "distinta"], ["/documenti", "documenti"], ["/profilo", "profilo"],
];

export default [
  // Le app a parte (src/apps): nell'app per telefoni l'elenco e' vuoto.
  ...allAppRoutes(),
  // Il launcher di chi amministra: pagina a se', senza il layout di nessuna app. Nell'app per telefoni
  // non c'e'. La guardia in router/index.js ci manda chi vede piu' di un'app, appena entrato.
  ...(process.env.VUE_APP_PIATTAFORMA === "mobile"
    ? []
    : [{
      path: "/hub",
      name: "hub",
      meta: { title: "Applicazioni", authRequired: true },
      component: () => import(/* webpackChunkName: "accessi" */ "../hub/HubView.vue"),
    }]),
  {
    path: "/oauth/airbnb/callback",
    name: "airbnb-oauth-callback",
    meta: {
      title: "Airbnb",
      authRequired: false,
    },
    component: () => import("../views/oauth/AirbnbCallback.vue"),
  },
  // Nell'app il cruscotto non c'e': la prima versione e' calendario, prenotazioni e messaggi.
  // Ma a '/' ci si arriva da molti punti — dopo l'accesso, dopo il secondo fattore, dalle guardie
  // del router, e dopo un cambio di spazio per i ruoli la cui pagina iniziale e' '/' (per gli
  // altri spazioService porta alla pagina iniziale del ruolo).
  // Invece di inseguirli uno a uno, la rotta stessa manda al calendario. Il nome 'default'
  // resta, perche' altre rotte lo usano.
  //
  // La condizione e' scritta per intero: webpack la valuta in compilazione, e nel sito resta
  // solo la rotta del cruscotto, identica a prima.
  process.env.VUE_APP_PIATTAFORMA === "mobile"
    ? { path: "/", name: "default", redirect: "/pms" }
    : {
      path: "/",
      name: "default",
      meta: {
        title: "Dashboard",
        authRequired: true,
      },
      component: () => import("../views/dashboard/index.vue"),
    },
  {
    path: "/properties",
    name: "properties",
    meta: {
      title: "Proprietà",
      authRequired: true,
    },
    component: () => import("../views/properties/index.vue"),
  },
  {
    // soloChiScrive: pagine che esistono solo per modificare. La sola area le aprirebbe anche a
    // chi la legge e basta, che ci troverebbe un modulo con un 403 al salvataggio: la guardia in
    // router/index.js lo porta invece alla sua pagina iniziale. Qui tocca al proprietario e al
    // collaboratore, che le strutture le leggono e non le scrivono.
    path: "/properties/new",
    name: "property-new",
    meta: {
      title: "Nuova Proprietà",
      authRequired: true,
      soloChiScrive: true,
    },
    component: () => import("../views/properties/form.vue"),
  },
  {
    path: "/properties/:id",
    name: "property-detail",
    meta: {
      title: "Dettaglio Proprietà",
      authRequired: true,
    },
    component: () => import("../views/properties/detail.vue"),
  },
  {
    path: "/properties/:id/edit",
    name: "property-edit",
    meta: {
      title: "Modifica Proprietà",
      authRequired: true,
      soloChiScrive: true,
    },
    component: () => import("../views/properties/form.vue"),
  },
  {
    path: "/messages",
    name: "messages",
    meta: {
      title: "Messaggi",
      authRequired: true,
    },
    component: () => import("../views/messages/index.vue"),
  },
  {
    path: "/pms",
    name: "pms",
    meta: {
      title: "Calendario & Canali",
      authRequired: true,
    },
    component: () => import("../views/pms/index.vue"),
  },
  {
    path: "/devices",
    name: "devices",
    meta: {
      title: "Dispositivi",
      authRequired: true,
    },
    component: () => import("../views/devices/index.vue"),
  },
  {
    path: "/devices/new",
    name: "device-new",
    meta: {
      title: "Nuovo Dispositivo",
      authRequired: true,
      soloChiScrive: true,
    },
    component: () => import("../views/devices/form.vue"),
  },
  {
    path: "/devices/:id/edit",
    name: "device-edit",
    meta: {
      title: "Modifica Dispositivo",
      authRequired: true,
      soloChiScrive: true,
    },
    component: () => import("../views/devices/form.vue"),
  },
  {
    // Il contenitore delle impostazioni. NIENTE requiredFeature: e' la pagina che raccoglie le
    // schede, e dentro c'e' anche la configurazione dei provider, che e' l'unica impostazione
    // che vede un cliente normale. Chiuderla dietro una funzione gliela toglierebbe.
    path: "/settings",
    name: "settings",
    meta: {
      title: "Impostazioni",
      authRequired: true,
    },
    component: () => import("../views/settings/index.vue"),
  },
  {
    // Nessuna requiredFeature nemmeno qui: la schermata si adatta da sola, e chi non amministra
    // lo spazio la vede in sola lettura invece di trovarsi una porta chiusa.
    path: "/settings/squadra",
    name: "squadra",
    meta: {
      title: "Squadra",
      authRequired: true,
    },
    component: () => import("../views/settings/squadra.vue"),
  },
  {
    // Come la squadra: nessuna requiredFeature. Chi non amministra lo spazio la vede in sola
    // lettura invece di trovarsi una porta chiusa.
    path: "/settings/notifiche",
    name: "notifiche",
    meta: {
      title: "Notifiche",
      authRequired: true,
    },
    component: () => import("../views/settings/notifiche.vue"),
  },
  {
    path: "/pulizie",
    name: "pulizie",
    meta: {
      title: "Pulizie",
      authRequired: true,
    },
    component: () => import("../views/pulizie/index.vue"),
  },
  {
    // Pubblica: chi riceve il link spesso non ha ancora un account. Il prefisso "/invito/" e'
    // anche in PERCORSI_PUBBLICI (keycloak.js), che usano la guardia del router e isOnPublicRoute:
    // senza, chi arriva finisce sul modulo d'accesso e il gettone si perde per strada.
    path: "/invito/:token",
    name: "invito",
    meta: {
      title: "Invito",
      authRequired: false,
    },
    component: () => import("../views/invito/index.vue"),
  },
  {
    path: "/settings/providers",
    name: "provider-configurations",
    meta: {
      title: "Configurazione Provider",
      authRequired: true,
    },
    component: () => import("../views/settings/providers.vue"),
  },
  {
    path: "/settings/providers/new",
    name: "provider-new",
    meta: {
      title: "Nuovo Provider",
      authRequired: true,
      soloChiScrive: true,
    },
    component: () => import("../views/settings/provider-form.vue"),
  },
  {
    path: "/settings/providers/:id",
    name: "provider-detail",
    meta: {
      title: "Dettaglio Provider",
      authRequired: true,
    },
    component: () => import("../views/settings/provider-detail.vue"),
  },
  {
    path: "/settings/providers/:id/edit",
    name: "provider-edit",
    meta: {
      title: "Modifica Provider",
      authRequired: true,
      soloChiScrive: true,
    },
    component: () => import("../views/settings/provider-form.vue"),
  },
  {
    path: "/task-queue",
    name: "task-queue",
    meta: {
      title: "Coda Operazioni",
      authRequired: true,
      requiredFeature: "taskQueue",
    },
    component: () => import("../views/task-queue/index.vue"),
  },
  {
    path: "/admin/operations",
    name: "admin-operations",
    meta: {
      title: "Operazioni Admin",
      authRequired: true,
      requiredFeature: "adminPanel",
    },
    component: () => import("../views/admin/operations.vue"),
  },
  {
    path: "/contract-templates",
    name: "contract-templates",
    meta: {
      title: "Modelli Contratto",
      authRequired: true,
      requiredFeature: "contractTemplates",
    },
    component: () => import("../views/contract-templates/index.vue"),
  },
  {
    path: "/modelli-messaggio",
    name: "modelli-messaggio",
    meta: {
      title: "Modelli di messaggio",
      authRequired: true,
      requiredFeature: "modelliMessaggio",
    },
    component: () => import("../views/modelli-messaggio/index.vue"),
  },
  {
    // I preventivi stanno accanto alle prenotazioni e non dentro: sono una cosa diversa -
    // tengono date senza essere ancora un impegno - e vanno guardati come elenco a se'.
    // Solo a chi le prenotazioni le scrive: un preventivo e' una trattativa in corso, e per chi
    // non puo' ne' confermarlo ne' annullarlo sarebbe un elenco di cose su cui non puo' fare niente.
    path: "/preventivi",
    name: "preventivi",
    meta: {
      title: "Preventivi",
      authRequired: true,
      soloChiScrive: true,
    },
    component: () => import("../views/prenotazione/preventivi.vue"),
  },
  {
    path: "/reservations",
    name: "reservations",
    meta: {
      title: "Prenotazioni",
      authRequired: true,
    },
    component: () => import("../views/reservations/index.vue"),
  },
  {
    path: "/rental-report",
    name: "rental-report",
    meta: {
      title: "Report Rendita",
      authRequired: true,
    },
    component: () => import("../views/rental-report/index.vue"),
  },
  {
    path: "/rental-report/trend",
    name: "rental-report-trend",
    meta: {
      title: "Andamento Rendita",
      authRequired: true,
    },
    component: () => import("../views/rental-report/trend.vue"),
  },
  // Il registro incassi (Fase C, faseC-registri.md §3.1, §12.1): del nucleo, in ogni spazio con le funzioni avanzate, dal primo
  // incasso (GC-2). requiredFeature: senza la chiave di /account/me (features.registroIncassi) la rotta porta alla home, come se
  // non ci fosse; area ECONOMICI (permessiService.PERCORSI, /contabilita), come /api/v1/pagamenti sul server.
  {
    path: "/contabilita/incassi",
    name: "contabilita-incassi",
    meta: {
      title: "Registro incassi",
      authRequired: true,
      requiredFeature: "registroIncassi",
    },
    component: () => import("../views/contabilita/registro-incassi.vue"),
  },
  {
    path: "/clienti",
    name: "clienti",
    meta: {
      title: "Clienti",
      authRequired: true,
    },
    component: () => import("../views/clienti/index.vue"),
  },
  {
    // La scheda si apre dall'elenco. L'identificativo e' quello del cliente, non della
    // prenotazione: la stessa persona ha piu' soggiorni e la scheda li tiene insieme.
    path: "/clienti/:id",
    name: "cliente-scheda",
    meta: {
      title: "Scheda cliente",
      authRequired: true,
    },
    component: () => import("../views/clienti/scheda.vue"),
  },
  {
    // I proprietari delle case: area ECONOMICI (permessiService.PERCORSI), senza requiredFeature
    // perche' l'anagrafica serve a tutti gli spazi, anche a chi gestisce senza partita IVA — il
    // contratto e i modelli dei messaggi leggono nome e codice fiscale da qui.
    path: "/proprietari",
    name: "proprietari",
    meta: {
      title: "Proprietari",
      authRequired: true,
    },
    component: () => import("../views/proprietari/index.vue"),
  },
  {
    // Prima di /proprietari/:id: "nuovo" non e' un identificativo. soloChiScrive perche' e' un
    // modulo vuoto, e chi l'anagrafica la legge soltanto ci troverebbe un 403 al salvataggio.
    path: "/proprietari/nuovo",
    name: "proprietario-nuovo",
    meta: {
      title: "Nuovo proprietario",
      authRequired: true,
      soloChiScrive: true,
    },
    component: () => import("../views/proprietari/scheda.vue"),
  },
  {
    path: "/proprietari/:id",
    name: "proprietario-scheda",
    meta: {
      title: "Scheda proprietario",
      authRequired: true,
    },
    component: () => import("../views/proprietari/scheda.vue"),
  },
  // Il modulo di gestione professionale (F2): mandati, rendiconti, ritenuta. Area ECONOMICI
  // (permessiService.PERCORSI) e requiredFeature: col modulo spento nello spazio le rotte portano
  // alla home, come se non ci fossero, e nessun componente del modulo si scarica.
  // meta.voceMenu (Fase 4): la voce del menu da accendere su una pagina che non ne ha una sua (la
  // linguetta Proprietari della Rendicontazione, le schede, la preparazione di un adempimento; menu.vue,
  // initActiveMenu). Solo su rotte del modulo: negli spazi senza modulo il menu resta quello di prima.
  {
    path: "/gestione/rendicontazione",
    name: "gestione-rendicontazione",
    meta: {
      title: "Rendicontazione",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/rendicontazione.vue"),
  },
  {
    path: "/gestione/rendicontazione/proprietari",
    name: "gestione-rendicontazione-proprietari",
    meta: {
      title: "Rendicontazione per proprietario",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
      voceMenu: "/gestione/rendicontazione",
    },
    component: () => import("../views/gestione/rendicontazione-proprietari.vue"),
  },
  {
    path: "/gestione/mandati",
    name: "gestione-mandati",
    meta: {
      title: "Mandati e configurazione",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/mandati.vue"),
  },
  // Le distinte, gli addebiti e il registro delle ritenute (F3, F4): come le rotte di F2, area ECONOMICI
  // (permessiService.PERCORSI ha gia' /gestione) e requiredFeature, quindi col modulo spento non ci sono.
  {
    path: "/gestione/distinte",
    name: "gestione-distinte",
    meta: {
      title: "Distinte",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/distinte.vue"),
  },
  {
    path: "/gestione/distinte/:id",
    name: "gestione-distinta",
    meta: {
      title: "Distinta",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
      voceMenu: "/gestione/distinte",
    },
    component: () => import("../views/gestione/distinta.vue"),
  },
  {
    path: "/gestione/addebiti",
    name: "gestione-addebiti",
    meta: {
      title: "Addebiti",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/addebiti.vue"),
  },
  {
    path: "/gestione/ritenute",
    name: "gestione-ritenute",
    meta: {
      title: "Registro ritenute",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/ritenute.vue"),
  },
  // I documenti fiscali (F5, §10.1 di f5-progetto.md): il registro, le sue viste per tipo (Fase 4), "Da fatturare" e la
  // scheda del documento. Come le rotte di F2-F4: area ECONOMICI e requiredFeature, quindi col modulo spento non ci sono.
  // I percorsi fissi stanno prima della scheda: non devono finire nel parametro :id.
  {
    path: "/gestione/documenti",
    name: "gestione-documenti",
    meta: {
      title: "Documenti",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/documenti.vue"),
  },
  {
    path: "/gestione/documenti/da-fatturare",
    name: "gestione-da-fatturare",
    meta: {
      title: "Da fatturare",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/da-fatturare.vue"),
  },
  // Fase 5 (fase5-documenti V1): le ricevute dei proprietari e le fatture del gestore agli ospiti di un periodo, in blocco.
  // Come "Da fatturare": un percorso fisso prima della scheda, area ECONOMICI e requiredFeature.
  {
    path: "/gestione/documenti/da-emettere-ospiti",
    name: "gestione-da-emettere-ospiti",
    meta: {
      title: "Da emettere agli ospiti",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/da-emettere-ospiti.vue"),
  },
  // Le voci del menu Documenti per tipo (Fase 4): la STESSA pagina del registro, con la vista fissata dal nome della
  // rotta (VISTE_DOCUMENTI in common/gestione.js). Percorsi fissi e non filtri nell'indirizzo: il menu accende una voce
  // solo se il percorso coincide. Prima della scheda, come "Da fatturare".
  {
    path: "/gestione/documenti/fatture",
    name: "gestione-documenti-fatture",
    meta: {
      title: "Fatture",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/documenti.vue"),
  },
  {
    path: "/gestione/documenti/ricevute",
    name: "gestione-documenti-ricevute",
    meta: {
      title: "Ricevute",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/documenti.vue"),
  },
  {
    path: "/gestione/documenti/note-di-credito",
    name: "gestione-documenti-note-di-credito",
    meta: {
      title: "Note di credito",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/documenti.vue"),
  },
  {
    path: "/gestione/documenti/invio-sdi",
    name: "gestione-documenti-invio-sdi",
    meta: {
      title: "Invio allo SDI",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/documenti.vue"),
  },
  // Fase A (faseA-documenti.md §7.2): i pro forma e le note non fiscali del gestore agli ospiti, una vista del registro come
  // le altre (VISTE_DOCUMENTI, "gestione-documenti-non-fiscali"). Prima della scheda, come le altre.
  {
    path: "/gestione/documenti/non-fiscali",
    name: "gestione-documenti-non-fiscali",
    meta: {
      title: "Pro forma e note non fiscali",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/documenti.vue"),
  },
  {
    path: "/gestione/documenti/:id",
    name: "gestione-documento",
    meta: {
      title: "Documento",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
      voceMenu: "/gestione/documenti",
    },
    component: () => import("../views/gestione/documento.vue"),
  },
  // Il portale dei proprietari, lato gestore (F6, §11.3 di f6-progetto.md): richieste, usi, email e accessi. Come le
  // altre rotte del modulo: area ECONOMICI (/gestione in permessiService) e requiredFeature.
  {
    path: "/gestione/portale",
    name: "gestione-portale",
    meta: {
      title: "Portale proprietari",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/portale.vue"),
  },
  // Gli adempimenti annuali e i contratti firmati (F7, §8 e §9.2 di f7-progetto.md). Come le altre rotte del modulo:
  // area ECONOMICI (/gestione in permessiService) e requiredFeature, quindi col modulo spento non ci sono. Il riepilogo
  // della tassa di soggiorno sta prima della preparazione: il suo percorso fisso non deve finire nei parametri.
  {
    path: "/gestione/adempimenti",
    name: "gestione-adempimenti",
    meta: {
      title: "Adempimenti",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/adempimenti.vue"),
  },
  {
    path: "/gestione/adempimenti/soggiorno",
    name: "gestione-soggiorno",
    meta: {
      title: "Tassa di soggiorno",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/soggiorno.vue"),
  },
  {
    path: "/gestione/adempimenti/:tipo/:anno",
    name: "gestione-adempimento",
    meta: {
      title: "Adempimento",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
      voceMenu: "/gestione/adempimenti",
    },
    component: () => import("../views/gestione/adempimento.vue"),
  },
  {
    path: "/gestione/contratti",
    name: "gestione-contratti",
    meta: {
      title: "Contratti firmati",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/contratti.vue"),
  },
  // La contabilita' del modulo (Fase C, faseC-registri.md §4-§7, §12.1): la riconciliazione degli incassi coi documenti, il
  // registro corrispettivi, le chiusure giornaliere con la scheda di una chiusura, la prima nota, gli incassi previsti. Come le
  // altre rotte del modulo: area ECONOMICI (/gestione in permessiService) e requiredFeature, quindi col modulo spento non ci sono.
  // Chiusure e prima nota sono dello spazio intero: chi vede una parte delle case non le ha nel menu, e il server risponde
  // GESTIONE_PERIMETRO_RISTRETTO (GC-4).
  {
    path: "/gestione/contabilita/riconciliazione",
    name: "gestione-riconciliazione",
    meta: {
      title: "Riconciliazione",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/contabilita/riconciliazione.vue"),
  },
  {
    path: "/gestione/contabilita/corrispettivi",
    name: "gestione-corrispettivi",
    meta: {
      title: "Registro corrispettivi",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/contabilita/corrispettivi.vue"),
  },
  {
    path: "/gestione/contabilita/chiusure",
    name: "gestione-chiusure",
    meta: {
      title: "Chiusure giornaliere",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/contabilita/chiusure.vue"),
  },
  {
    path: "/gestione/contabilita/chiusure/:id",
    name: "gestione-chiusura",
    meta: {
      title: "Chiusura",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
      voceMenu: "/gestione/contabilita/chiusure",
    },
    component: () => import("../views/gestione/contabilita/chiusura.vue"),
  },
  {
    path: "/gestione/contabilita/prima-nota",
    name: "gestione-prima-nota",
    meta: {
      title: "Prima nota",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/contabilita/prima-nota.vue"),
  },
  {
    path: "/gestione/contabilita/incassi-previsti",
    name: "gestione-incassi-previsti",
    meta: {
      title: "Incassi previsti",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: () => import("../views/gestione/contabilita/incassi-previsti.vue"),
  },
  // "Vedi come il proprietario" (§5.5): le STESSE viste del portale, con la base delle chiamate che il percorso
  // sceglie (common/portale.js, baseApi). Sotto /gestione, quindi area ECONOMICI del gestore, e col modulo spento
  // non ci sono. Nel chunk del portale, come le viste del proprietario.
  ...ROTTE_PORTALE.map(([sotto, vista]) => ({
    path: `/gestione/portale/anteprima/:proprietarioId${sotto}`,
    name: `gestione-portale-anteprima-${vista}`,
    meta: {
      title: "Anteprima del portale",
      authRequired: true,
      requiredFeature: "gestioneProfessionale",
    },
    component: VISTE_PORTALE[vista],
  })),
  {
    path: "/reservations/:propertyId/:reservationId",
    name: "reservation-detail",
    meta: {
      title: "Dettaglio Prenotazione",
      authRequired: true,
    },
    component: () => import("../views/reservations/detail.vue"),
  },
  {
    path: "/access-links",
    name: "access-links",
    meta: {
      title: "Codici Accesso",
      authRequired: true,
    },
    component: () => import("../views/access-links/index.vue"),
  },
  // Il portale del proprietario (F6): solo per chi ha un accesso del portale nello spazio attivo (features.
  // portaleProprietario, da /account/me). Area ACCOUNT (permessiService.PERCORSI, la stessa riga di MappaAree), e la
  // guardia del router (router/index.js) porta qui chi ha l'accesso, da qualunque altra pagina. Negli spazi senza
  // modulo, e per il proprietario invitato dalla Squadra, la funzione e' falsa e queste rotte portano alla home.
  ...ROTTE_PORTALE.map(([sotto, vista]) => ({
    path: `/portale${sotto}`,
    name: `portale-${vista}`,
    meta: {
      title: "Portale proprietari",
      authRequired: true,
      requiredFeature: "portaleProprietario",
    },
    component: VISTE_PORTALE[vista],
  })),
  {
    path: "/profile",
    name: "profile",
    meta: {
      title: "Profile Settings",
      authRequired: true,
    },
    component: () => import("../views/account/profile.vue"),
  },
  {
    path: "/settings/pricelabs",
    name: "pricelabs",
    meta: {
      title: "PriceLabs",
      authRequired: true,
      requiredFeature: "pricelabs",
    },
    component: () => import("../views/settings/pricelabs.vue"),
  },
  {
    path: "/settings/chiavi-api",
    name: "chiavi-api",
    meta: {
      title: "Chiavi API",
      authRequired: true,
    },
    component: () => import("../views/settings/chiavi-api.vue"),
  },
  {
    path: "/settings/canali-messaggistica",
    name: "canali-messaggistica",
    meta: {
      title: "Canali di messaggistica",
      authRequired: true,
      requiredFeature: "canaliMessaggistica",
    },
    component: () => import("../views/settings/canali-messaggistica.vue"),
  },
  {
    path: "/settings/test-funzionalita",
    name: "test-funzionalita",
    meta: {
      title: "Check-in AI Test",
      authRequired: true,
      requiredFeature: "testFunzionalita",
    },
    component: () => import("../views/settings/test-funzionalita.vue"),
  },
  {
    // L'interruttore del modulo e i dati del gestore. Una feature a parte da gestioneProfessionale:
    // a modulo spento la pagina serve proprio ad accenderlo, e la vede solo il titolare di uno
    // spazio ammesso dal server (zoelia.gestione.attivabile-per).
    path: "/settings/gestione-professionale",
    name: "gestione-impostazioni",
    meta: {
      title: "Gestione professionale",
      authRequired: true,
      requiredFeature: "gestioneProfessionaleImpostazioni",
    },
    component: () => import("../views/gestione/impostazioni.vue"),
  },
  {
    path: "/settings/stripe-accounts",
    name: "stripe-accounts",
    meta: { title: "Account Stripe", authRequired: true, requiredFeature: "stripeAccounts" },
    component: () => import("../views/settings/stripe-accounts.vue"),
  },
  {
    path: "/settings/stripe-accounts/new",
    name: "stripe-account-new",
    meta: { title: "Nuovo account Stripe", authRequired: true, requiredFeature: "stripeAccounts", soloChiScrive: true },
    component: () => import("../views/settings/stripe-account-form.vue"),
  },
  {
    path: "/settings/stripe-accounts/:id",
    name: "stripe-account-detail",
    meta: { title: "Dettaglio account Stripe", authRequired: true, requiredFeature: "stripeAccounts" },
    component: () => import("../views/settings/stripe-account-form.vue"),
  },
  {
    // Il catalogo dei servizi da vendere al check-in (Fase B): di tutti gli spazi, ma dietro le funzioni avanzate come i
    // pagamenti (revisione B26). Chi non legge i conti del gestore lo rimanda la pagina (vedi il suo mounted).
    path: "/settings/servizi-aggiuntivi",
    name: "servizi-aggiuntivi",
    meta: { title: "Servizi aggiuntivi", authRequired: true, requiredFeature: "serviziAggiuntivi" },
    component: () => import("../views/settings/servizi-aggiuntivi.vue"),
  },
  {
    // Le condizioni delle prenotazioni dirette (Fase D, faseD-condizioni.md K1): di tutti gli spazi, ma dietro le funzioni avanzate
    // come i pagamenti (revisione D16). Il percorso e' ECONOMICI per permessiService: il collaboratore lo rimanda la guardia.
    path: "/settings/condizioni-dirette",
    name: "condizioni-dirette",
    meta: { title: "Condizioni delle dirette", authRequired: true, requiredFeature: "condizioniPrenotazione" },
    component: () => import("../views/settings/condizioni-dirette.vue"),
  },
  {
    // I conti di incasso (Fase G, faseG-incassi.md §5.1): dove arrivano i soldi degli incassi a mano, di tutti gli spazi ma dietro
    // le funzioni avanzate come i pagamenti, accanto agli account Stripe. Il percorso e' ECONOMICI per permessiService: il
    // collaboratore lo rimanda la guardia; chi non vede tutte le case li legge e basta (lo decide la pagina).
    path: "/settings/conti-incasso",
    name: "conti-incasso",
    meta: { title: "Conti di incasso", authRequired: true, requiredFeature: "contiIncasso" },
    component: () => import("../views/settings/conti-incasso.vue"),
  },
  {
    path: "/checkin/:bookingReference",
    name: "checkin",
    meta: {
      title: "Online Check-in",
    },
    component: () => import("../views/checkin/index.vue"),
  },
  {
    // La pagina del link di pagamento: la apre chi deve pagare, che non ha un account.
    // Senza authRequired, come il check-in e la pagina di accesso — e ricordarsi che la
    // guardia di Keycloak la lascia passare solo se '/paga/' e' anche in PERCORSI_PUBBLICI,
    // in keycloak.js (la usa router/index.js).
    path: "/paga/:token",
    name: "paga",
    meta: {
      title: "Pagamento",
    },
    component: () => import("../views/pagamenti/paga.vue"),
  },
  {
    path: "/access/:slug",
    name: "access",
    meta: {
      title: "Access Page",
    },
    component: () => import("../views/access/index.vue"),
  },
  {
    path: "/access/:slug/house-rules",
    name: "access-house-rules",
    meta: {
      title: "House Rules",
    },
    component: () => import("../views/access/house-rules.vue"),
  },
  {
    path: "/login",
    name: "login",
    component: () => import("../views/account/login.vue"),
    meta: {
      title: "Login",
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters["auth/loggedIn"]) {
          // Redirect to the home page instead
          next({ name: "default" });
          next();
        } else {
          // Continue to the login page
          next();
        }
      },
    },
  },
  // Registration disabled - users are added manually in Keycloak
  // {
  //   path: "/register",
  //   name: "Register",
  //   component: () => import("../views/account/register.vue"),
  //   meta: {
  //     title: "Register",
  //   },
  // },
  {
    /**
     * Il codice di verifica, subito dopo la password.
     *
     * <p>Rotta sua e non un passo dentro /login: a questo punto la persona e' autenticata, e la
     * guardia di /login rimanda alla dashboard chi ha una sessione valida. Senza una rotta
     * propria, un ricaricamento a metta' accesso finirebbe su una dashboard che risponde 403 a
     * ogni chiamata, senza spiegare perche'.
     */
    path: "/secondo-fattore",
    name: "secondo-fattore",
    component: () => import("../views/account/secondo-fattore.vue"),
    meta: {
      title: "Verifica in due passaggi",
      authRequired: true,
    },
  },
  {
    path: "/forgot-password",
    name: "Forgot password",
    component: () => import("../views/account/forgot-password.vue"),
    meta: {
      title: "Forgot Password",
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters["auth/loggedIn"]) {
          // Redirect to the home page instead
          next({ name: "default" });
        } else {
          // Continue to the login page
          next();
        }
      },
    },
  },
  {
    path: "/logout",
    name: "logout",
    meta: {
      title: "Logout",
      beforeResolve() {
        // Nell'app tema e lingua sono impostazioni del telefono, non della sessione: svuotando
        // tutto, dopo ogni «Esci» l'app tornerebbe chiara e nella lingua della build. Si
        // conservano solo quei due. Il gettone del dispositivo fidato invece se ne va come nel
        // sito: dopo un'uscita volontaria, chi rientra da quel telefono ridia il codice.
        // Restano anche due segni delle notifiche push, che riguardano il telefono e non chi e'
        // entrato: la spiegazione del permesso gia' mostrata (senza, la si ripresenterebbe a ogni
        // accesso) e gli ultimi tocchi gestiti (senza, un tocco vecchio riconsegnato da Android
        // dopo l'uscita riporterebbe di nuovo a quella pagina). Il gettone FCM non e' qui: sta
        // solo in memoria, e all'uscita lo cancella il gancio di notifichePush.
        //
        // Nel sito come nell'app resta anche il resoconto dell'ultima uscita (diagnosticaUscita):
        // se un'uscita automatica non e' ancora stata raccontata al server, e' proprio quella
        // da capire, e questa pulizia la cancellerebbe.
        const conserva = [
          CHIAVE_ULTIMA_USCITA,
          ...(process.env.VUE_APP_PIATTAFORMA === "mobile"
            ? ["zoelia_tema", "zoelia-locale", "zoelia_push_chiesto", "zoelia_push_toccate"]
            : []),
        ].map((k) => [k, localStorage.getItem(k)]);
        // Clear local storage and session
        localStorage.clear();
        conserva.forEach(([k, v]) => { if (v !== null) localStorage.setItem(k, v); });
        sessionStorage.clear();
        // Clear Vuex auth state
        store.commit("auth/SET_CURRENT_USER", null);
        // Logout from Keycloak and redirect to login. E' l'«Esci» della persona: nell'app
        // revoca anche la sessione sul server.
        keycloakService.logout("esci");
        // Don't continue navigation - logout will redirect
      },
    },
    component: () => import("../views/account/login.vue")
  },
  {
    path: '/auth/signin-basic',
    name: 'signin-basic',
    meta: {
      title: "Signin Basic", authRequired: true,
    },
    component: () => import('../views/auth/signin/basic')
  },
  {
    path: '/auth/signin-cover',
    name: 'signin-cover',
    meta: {
      title: "Signin Cover", authRequired: true,
    },
    component: () => import('../views/auth/signin/cover')
  },
  {
    path: '/auth/signup-basic',
    name: 'signup-basic',
    meta: {
      title: "Signup Basic", authRequired: true,
    },
    component: () => import('../views/auth/signup/basic')
  },
  {
    path: '/auth/signup-cover',
    name: 'signup-cover',
    meta: {
      title: "Signup Cover", authRequired: true,
    },
    component: () => import('../views/auth/signup/cover')
  },
  {
    path: '/auth/404',
    name: '404',
    meta: {
      title: "Error 404", authRequired: true,
    },
    component: () => import('../views/auth/errors/404')
  },
  {
    path: '/auth/500',
    name: '500',
    meta: {
      title: "Error 500", authRequired: true,
    },
    component: () => import('../views/auth/errors/500')
  },
  {
    path: '/auth/404-basic',
    name: '404-basic',
    meta: {
      title: "Error 404", authRequired: true,
    },
    component: () => import('../views/auth/errors/404-basic')
  },
  {
    path: '/auth/404-cover',
    name: '404-cover',
    meta: {
      title: "Error 404", authRequired: true,
    },
    component: () => import('../views/auth/errors/404-cover')
  },
  {
    path: '/auth/ofline',
    name: 'ofline',
    meta: {
      title: "Offline", authRequired: true,
    },
    component: () => import('../views/auth/errors/ofline')
  },
  {
    path: '/auth/logout-basic',
    name: 'logout-basic',
    meta: {
      title: "Logout", authRequired: true,
    },
    component: () => import('../views/auth/logout/basic')
  },
  {
    path: '/auth/logout-cover',
    name: 'logout-cover',
    meta: {
      title: "Logout", authRequired: true,
    },
    component: () => import('../views/auth/logout/cover')
  },
];